import React, { useCallback, useMemo, useState } from 'react';
import { HelperText, useTheme } from 'react-native-paper';
import { useMutation, useQueryClient } from 'react-query';
import type { ApplePayError, ConfirmPaymentError, GooglePayError, StripeError } from '@stripe/stripe-react-native';
import { CompositeScreenProps, useIsFocused } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { Alert } from 'react-native';
import { useI18n } from '../../context/I18nContext';
import { flattenDict } from '../../utils/helpers';
import {
  retrieveSubscriptionClientSecret,
  createSubscription,
  invalidateSubscriptionData,
  useGetProductById,
} from '../../apis/subscriptionApis';
import { PaymentMethodOption } from '../../types/misc.types';
import { useStripe } from '../../payment/utils/stripe';
import ConfirmationRow from '../../payment/components/ConfirmationRow';
import Container from '../../components/Container';
import { NavigationDialog } from '../CommonDialogs';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { CardPaymentMethod, useActivePayment } from '../../payment/MapViewActivePaymentContext';
import { trackMakeSubscription } from '../../utils/mixpanelUtils';
import { useMixpanel } from '../../mixpanel/MixpanelContext';
import * as Sentry from '../../components/sentry/sentry';
import {
  Currency,
  Product,
  ProductRenewInterval,
  ProductStatus,
  ProductSubscription,
  ProductSubscriptionStatus,
} from '../../types/appsync-types';
import { SubscriptionsStackRouteParams } from '../../navigators/SubscriptionsStackNavigator';
import { getRenewIntervalTranslationKeys } from '../../utils/getRenewIntervalTranslationKeys';
import { AgreeToTermsRow } from '../../payment/components/AgreeToTermsRow';
import { screenNames } from '../../navigators/screenNames';
import { useParkingTermsUrlForCurrentLocale } from '../../hooks/useParkingTermsUrlForCurrentLocale';

export interface ConfirmSubscriptionDialogRouteParams {
  id: string;
}

export type SubscriptionFailureHandler = (
  type:
    | 'PAYMENT'
    | 'CANCELLED'
    | 'STRIPE_SUBSCRIPTION_ID_MISSING'
    | 'PAYMENT_SECRET_MISSING'
    | 'UNEXPECTED'
    | 'UNEXPECTED_CONFIRMATION',
  data: ProductSubscription | undefined | null,
  stripeError?: StripeError<ConfirmPaymentError | GooglePayError | ApplePayError>,
) => void;

type Props = CompositeScreenProps<
  StackScreenProps<SubscriptionsStackRouteParams, 'ConfirmSubscriptionDialog'>,
  AppDrawerScreenProp
>;

export function ConfirmSubscriptionDialog({ navigation, route }: Props) {
  const isScreenFocused = useIsFocused();
  const { activePaymentMethod, isActivePaymentMethodLoading } = useActivePayment();
  const { colors } = useTheme();
  const mp = useMixpanel();
  const queryClient = useQueryClient();
  const stripe = useStripe();
  const { I18n, formatCurrency } = useI18n();
  const [userAgreedToTerms, setUserAgreedToTerms] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const getProductByIdResult = useGetProductById(route.params.id);

  // TODO: Handle product being undefined
  const product = getProductByIdResult.data?.data?.getProductById;
  const subscription = product?.subscription;
  const termsUrl = useParkingTermsUrlForCurrentLocale(product?.terms);
  const isPaymentConfirmationFlow = subscription?.status === ProductSubscriptionStatus.REQUIRES_ACTION;

  const handleUserDismissed = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const onSubscriptionSucceeded = useCallback(
    (makeAProductSubscription: ProductSubscription, selectedProduct: Product) => {
      trackMakeSubscription(mp, makeAProductSubscription, selectedProduct);

      // Sparing a bit time to try to eliminate the async update of subscription
      setTimeout(() => {
        invalidateSubscriptionData(queryClient);
      }, 1000);
      // TODO: Make sure, if possible, that user can't come back to this screen by navigating forward
      navigation.goBack();
    },
    [mp, navigation, queryClient],
  );

  const handleSubscriptionFailed: SubscriptionFailureHandler = useCallback(
    (type, makeAProductSubscription, stripeError) => {
      mp?.getPeople().increment('Subscription failures', 1);
      if (type === 'CANCELLED') {
        mp?.track('Subscription creation cancelled', flattenDict('subscription', makeAProductSubscription));
        setErrorMessage(
          `${I18n.t('subscriptions.confirmSubscriptionModal.subscriptionCancelled.title')}: ${I18n.t(
            'subscriptions.confirmSubscriptionModal.subscriptionCancelled.description',
          )}`,
        );
      } else if (type === 'PAYMENT_SECRET_MISSING') {
        mp?.track(
          'Subscription payment confirmation failed due to payment secret missing',
          flattenDict('subscription', makeAProductSubscription),
        );
        setErrorMessage(`${I18n.t('subscriptions.confirmSubscriptionModal.subscriptionFailMainContent')}`);
      } else if (type === 'STRIPE_SUBSCRIPTION_ID_MISSING') {
        mp?.track(
          'Subscription payment confirmation failed due to Stripe subscription id missing',
          flattenDict('subscription', makeAProductSubscription),
        );
        setErrorMessage(`${I18n.t('subscriptions.confirmSubscriptionModal.subscriptionFailMainContent')}`);
      } else if (stripeError) {
        mp?.track('Subscription creation failed', flattenDict('error', stripeError));
        setErrorMessage(stripeError.localizedMessage ?? stripeError.message);
      } else if (type === 'UNEXPECTED_CONFIRMATION') {
        mp?.track('Subscription payment confirmation failed', flattenDict('subscription', makeAProductSubscription));
        setErrorMessage(`${I18n.t('subscriptions.confirmSubscriptionModal.subscriptionFailMainContent')}`);
      } else {
        mp?.track('Subscription creation failed', flattenDict('subscription', makeAProductSubscription));
        setErrorMessage(`${I18n.t('subscriptions.confirmSubscriptionModal.subscriptionFailMainContent')}`);
      }
    },
    [mp, I18n],
  );

  const handleSubscriptionPaymentConfirmationWithCard = useCallback(
    async (paymentSecret: string, createdSubscription: ProductSubscription): Promise<void> => {
      try {
        const confirmPaymentResult = await stripe.confirmPayment(paymentSecret, {
          paymentMethodType: 'Card',
          paymentMethodData: { paymentMethodId: (activePaymentMethod as CardPaymentMethod).stripeCard.id },
        });
        if (confirmPaymentResult.paymentIntent) {
          // TODO: properly handle product being null | undefined
          onSubscriptionSucceeded(createdSubscription, product!);
        } else {
          throw confirmPaymentResult.error;
        }
      } catch (error) {
        console.error('Payment failed', error);
        mp?.getPeople().increment('Payment failures', 1);
        mp?.track('Subscription creation payment failed', {
          ...flattenDict('subscription', createdSubscription),
          ...flattenDict('error', error),
        });
        Sentry.captureException(error);
        handleSubscriptionFailed('PAYMENT', createdSubscription, error as StripeError<ConfirmPaymentError>);
      }
    },
    [stripe, activePaymentMethod, onSubscriptionSucceeded, product, mp, handleSubscriptionFailed],
  );

  const retrieveSubscriptionClientSecretMutation = useMutation(
    async (createdSubscription: ProductSubscription) => {
      if (!createdSubscription.stripeSubscriptionId) {
        throw new Error('No Stripe subscription id!');
      }
      return retrieveSubscriptionClientSecret({ subscriptionId: createdSubscription.id });
    },
    {
      async onSuccess(data, createdSubscription) {
        const paymentSecret = data?.data?.retrieveSubscriptionClientSecret;
        if (!paymentSecret) {
          return handleSubscriptionFailed('PAYMENT_SECRET_MISSING', createdSubscription);
        }

        // TODO: Possibly add separate function to handle confirmation with Google/Apple pay? (See ConfirmReservationDialog for reference)
        await handleSubscriptionPaymentConfirmationWithCard(paymentSecret, createdSubscription);
      },
      onError(error: any) {
        const errorCode = error?.errors?.[0]?.message;
        console.error(errorCode);
        handleSubscriptionFailed('UNEXPECTED_CONFIRMATION', null);
      },
    },
  );

  const handleSubscriptionWithCard = useCallback(
    async (createdSubscription: ProductSubscription): Promise<void> => {
      try {
        const confirmPaymentResult = { paymentIntent: true, error: null };
        // const confirmPaymentResult = await stripe.confirmPayment(createdSubscription?.paymentSecret!, {
        //   paymentMethodType: 'Card',
        //   paymentMethodData: { paymentMethodId: (activePaymentMethod as CardPaymentMethod).stripeCard.id },
        // });
        if (confirmPaymentResult.paymentIntent) {
          // TODO: properly handle product being null | undefined
          onSubscriptionSucceeded(createdSubscription, product!);
        } else {
          throw confirmPaymentResult.error;
        }
      } catch (error) {
        console.error('Payment failed', error);
        mp?.getPeople().increment('Payment failures', 1);
        mp?.track('Subscription creation payment failed', {
          ...flattenDict('subscription', createdSubscription),
          ...flattenDict('error', error),
        });
        Sentry.captureException(error);
        handleSubscriptionFailed('PAYMENT', createdSubscription, error as StripeError<ConfirmPaymentError>);
      }
    },
    [onSubscriptionSucceeded, product, mp, handleSubscriptionFailed],
  );

  const handleSubscriptionAction = useCallback(
    async (productSubscription: ProductSubscription | undefined | null) => {
      switch (productSubscription?.status) {
        case ProductSubscriptionStatus.FAILED:
          handleSubscriptionFailed('PAYMENT', productSubscription);
          break;
        case ProductSubscriptionStatus.MISSING_PAYMENT:
          mp?.track('Subscription creation pending payment', flattenDict('subscription', productSubscription));
          // activePaymentMethod can not be null, because user cannot click payment button without active payment method
          if (!activePaymentMethod) {
            Alert.alert(I18n.t('payment.alert.noActivePaymentMethod'));
            return;
          }
          if (activePaymentMethod.paymentMethodType === PaymentMethodOption.CARD) {
            await handleSubscriptionWithCard(productSubscription);
          }
          break;

        case ProductSubscriptionStatus.REQUIRES_ACTION:
          mp?.track('Subscription requires action', flattenDict('subscription', productSubscription));
          await retrieveSubscriptionClientSecretMutation.mutateAsync(productSubscription);
          break;

        case ProductSubscriptionStatus.ACTIVE:
          // TODO: properly handle product being null | undefined
          onSubscriptionSucceeded(productSubscription, product!);
          break;
        default:
          handleSubscriptionFailed('UNEXPECTED', productSubscription);
          break;
      }
    },
    [
      I18n,
      activePaymentMethod,
      handleSubscriptionFailed,
      handleSubscriptionWithCard,
      mp,
      onSubscriptionSucceeded,
      product,
      retrieveSubscriptionClientSecretMutation,
    ],
  );

  const makeASubscriptionMutation = useMutation(
    async (input: Parameters<typeof createSubscription>) => {
      console.log('input', input);
      return createSubscription(...input);
    },
    {
      async onSuccess(data) {
        handleSubscriptionAction(data.data?.makeAProductSubscription);
      },
      onError(error: any) {
        /* Could not do pre-payment-booking */
        const errorCode = error?.errors?.[0]?.message;
        console.error(errorCode);
        handleSubscriptionFailed('UNEXPECTED', null);
      },
      onSettled() {
        // Sparing a bit time to try to eliminate the async update of subscription
        setTimeout(() => {
          invalidateSubscriptionData(queryClient);
        }, 1000);
      },
    },
  );

  const onConfirm = useCallback(async () => {
    if (!product) throw new Error('Product is undefined!');

    if (!subscription && activePaymentMethod?.paymentMethodType === PaymentMethodOption.CARD) {
      // The actual logic of what to do after this, is in the mutations' success / fail handlers.
      await makeASubscriptionMutation.mutateAsync([
        {
          input: {
            productId: product.id,
            cardId: activePaymentMethod.stripeCard.id,
          },
        },
      ]);
    } else if (isPaymentConfirmationFlow) {
      handleSubscriptionAction(subscription);
    } else {
      setErrorMessage(`${I18n.t('payment.alert.noActivePaymentMethod')}`);
    }
  }, [
    product,
    subscription,
    activePaymentMethod,
    isPaymentConfirmationFlow,
    makeASubscriptionMutation,
    handleSubscriptionAction,
    I18n,
  ]);

  if (!isScreenFocused) {
    return null;
  }

  return (
    <NavigationDialog
      style={{ backgroundColor: colors.elevation.level2 }} // TODO: is this correct?
      title={
        subscription
          ? I18n.t('subscriptions.confirmSubscriptionModal.paymentRetryTitle')
          : I18n.t('subscriptions.confirmSubscriptionModal.title')
      }
      description={''}
      onDismiss={handleUserDismissed}
      actions={[
        {
          title: I18n.t('subscriptions.confirmSubscriptionModal.cancel'),
          onPress: handleUserDismissed,
          disable: makeASubscriptionMutation.isLoading || retrieveSubscriptionClientSecretMutation.isLoading,
        },
        {
          title: I18n.t('subscriptions.confirmSubscriptionModal.confirm'),
          loading:
            makeASubscriptionMutation.isLoading ||
            isActivePaymentMethodLoading ||
            getProductByIdResult.isLoading ||
            retrieveSubscriptionClientSecretMutation.isLoading,
          disable: !product || (!userAgreedToTerms && !subscription) || (!!subscription && !isPaymentConfirmationFlow),
          onPress: () => {
            return onConfirm();
          },
        },
      ]}
      iconName={'check'}
    >
      {/* Product */}
      {/* TODO: What icon? */}
      <ConfirmationRow title={product?.name ?? '?'} iconName="calendar" />

      <ConfirmationRow
        iconName="wallet"
        title={
          formatCurrency(product?.price, Currency.EUR) +
          ' / ' +
          I18n.t(getRenewIntervalTranslationKeys(product?.renewInterval ?? ProductRenewInterval.MONTHLY).singular)
        }
      />

      {!subscription && (
        <AgreeToTermsRow
          text={I18n.t('subscriptions.confirmSubscriptionModal.terms')}
          onValueChange={setUserAgreedToTerms}
          onTermsClick={() => {
            // TODO: What to do if terms aren't defined?
            if (termsUrl) {
              navigation.navigate(screenNames.ParkingTerms, {
                url: termsUrl,
              });
            }
          }}
          value={userAgreedToTerms}
        />
      )}

      {/* Error text */}
      <Container>
        <HelperText type={'error'} visible={!!errorMessage}>
          {errorMessage}
        </HelperText>
      </Container>
    </NavigationDialog>
  );
}
